import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { FieldInspectionService, InspectorUser } from '../../../field-inspection/services/field-inspection.service';
import { SeasonManagerService } from '../../../services/season-manager.service';

export type DialogMode = 'LIST' | 'NEW_INSPECTION' | 'FORM';

export interface CropDefinition {
  id: number;
  crop: string;
  fieldType: string;
  label: string;
}

export interface FieldInspectionDialogData {
  pinId: number;
  fieldId: number;
  farmId?: number;
  tenantId: number;
  seasonId?: number | null;
  farm: string;
  field: string;
  lat: number;
  lng: number;
  viewOnly?: boolean;
  isNewInspection?: boolean;
  cropDefinitions?: CropDefinition[];
}

@Component({
  selector: 'app-field-inspection-dialog',
  templateUrl: './field-inspection-dialog.component.html',
  styleUrls: ['./field-inspection-dialog.component.css'],
  standalone: false
})
export class FieldInspectionDialogComponent implements OnInit {

  mode: DialogMode = 'LIST';

  // New Inspection dialog state (combined crop + inspector)
  inspectors: InspectorUser[] = [];
  loadingInspectors = false;
  selectedInspectorId: number | null = null;
  selectedCropDefinitionId: number | null = null;

  // Passed into form
  resolvedInspectorUserId: number | null = null;
  resolvedCropDefinitionId: number | null = null;
  resolvedCrop: string = '';
  resolvedFieldType: string = '';
  resolvedReportId: string | null = null;
  resolvedViewOnly: boolean = false;

  // Dirty-state tracking from form child
  formIsDirty = false;
  resolvingCropDefs = false;
  /** True while we are loading report detail + season meta before entering FORM mode */
  resolvingReport = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FieldInspectionDialogData,
    public dialogRef: MatDialogRef<FieldInspectionDialogComponent>,
    private inspectionService: FieldInspectionService,
    private seasonManagerService: SeasonManagerService,
    private dialog: MatDialog,
    public nav: NavBarComponent
  ) {
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.nav.toggleLoading(false);
    if (this.data.isNewInspection) {
      this.openNewInspectionDialog();
    } else if ((this.data as any)._openReportId) {
      // Opened directly from tenant list — resolve crop/fieldType from report detail then go to FORM
      const reportId = (this.data as any)._openReportId as string;
      this.resolvedReportId = reportId;
      this.resolvedViewOnly = this.data.viewOnly ?? false;
      this.resolvingReport = true;

      // Load report detail to get fieldId, then resolve crop/fieldType via season API
      this.inspectionService.loadReportDetail(reportId).subscribe({
        next: (report) => {
          const fieldId = report.fieldId;
          if (!fieldId) {
            this.resolvingReport = false;
            this.mode = 'FORM';
            return;
          }
          forkJoin({
            meta: this.seasonManagerService.getSeasonMetaData().pipe(catchError(() => of(null))),
            detail: this.seasonManagerService.getDetailsOfFieldById(fieldId).pipe(catchError(() => of(null)))
          }).subscribe(({ meta, detail }) => {
            const fieldTypes: any[] = meta?.seasonFieldType ?? [];
            const cropTypes: any[] = meta?.seasonFieldCrop ?? [];
            const rawDetails: any[] = (detail?.details?.length > 0)
              ? detail.details
              : (detail?.detail ? [detail.detail] : []);
            const first = rawDetails[0];
            if (first) {
              const ftId = first.fieldType ?? first.fieldTypeId;
              const cropId = first.currentCrop ?? first.currentCropId;
              this.resolvedFieldType = fieldTypes.find((t: any) => t.id === ftId)?.description
                ?? (ftId ? String(ftId) : '');
              this.resolvedCrop = cropTypes.find((c: any) => c.id === cropId)?.description
                ?? (cropId ? String(cropId) : '');
            }
            this.resolvingReport = false;
            this.mode = 'FORM';
          });
        },
        error: () => {
          this.resolvingReport = false;
          this.mode = 'FORM';
        }
      });
    } else {
      this.mode = 'LIST';
    }
  }

  // ─── New Inspection combined dialog ──────────────────────────────────────

  openNewInspectionDialog(): void {
    const defs = this.data.cropDefinitions;

    if (defs !== undefined && defs.length === 0) {
      this.showPreconditionError();
      return;
    }

    // cropDefinitions known — proceed directly
    if (defs !== undefined) {
      if (defs.length === 1) {
        this.selectedCropDefinitionId = defs[0].id;
      }
      this.loadInspectors();
      this.mode = 'NEW_INSPECTION';
      return;
    }

    // cropDefinitions undefined (e.g. map entry point) — resolve from season API
    this.resolvingCropDefs = true;
    this.mode = 'NEW_INSPECTION'; // show dialog immediately with loading state

    forkJoin({
      meta: this.seasonManagerService.getSeasonMetaData().pipe(catchError(() => of(null))),
      detail: this.seasonManagerService.getDetailsOfFieldById(this.data.fieldId).pipe(catchError(() => of(null)))
    }).subscribe(({ meta, detail }) => {
      this.resolvingCropDefs = false;

      if (!detail) {
        // API call failed entirely — can't validate preconditions, show error
        this.showPreconditionError();
        return;
      }

      const fieldTypes: any[] = meta?.seasonFieldType ?? [];
      const cropTypes: any[] = meta?.seasonFieldCrop ?? [];

      // Use details array if available, fall back to single detail object
      const rawDetails: any[] = (detail?.details?.length > 0)
        ? detail.details
        : (detail?.detail ? [detail.detail] : []);

      if (rawDetails.length === 0) {
        this.showPreconditionError();
        return;
      }

      const resolved: CropDefinition[] = rawDetails.map(d => {
        const ftId = d.fieldType ?? d.fieldTypeId;
        const cropId = d.currentCrop ?? d.currentCropId;

        // Descriptions only when the meta lookup actually resolved them. These are stored
        // against the report and used to scope its form config, so an invented value would
        // mislabel the inspection — an empty one lets the API derive instead.
        const fieldTypeDesc = fieldTypes.find((t: any) => t.id === ftId)?.description ?? '';
        const cropDesc = cropTypes.find((c: any) => c.id === cropId)?.description ?? '';

        // The picker label may still fall back to the raw ids, so an unresolved definition
        // stays visible and selectable rather than rendering blank.
        const fieldTypeLabel = fieldTypeDesc || (ftId ? `Type ${ftId}` : '');
        const cropLabel = cropDesc || (cropId ? `Crop ${cropId}` : '');

        return {
          id: d.id,
          crop: cropDesc,
          fieldType: fieldTypeDesc,
          label: (cropLabel && fieldTypeLabel)
            ? `${cropLabel} (${fieldTypeLabel})`
            : (cropLabel || fieldTypeLabel || `Definition ${d.id ?? ''}`)
        };
      });

      // Only show precondition error if NONE of the definitions have any crop/fieldType
      // (i.e. genuinely not configured — not just a meta lookup failure)
      const hasValidDef = rawDetails.some(d =>
        (d.fieldType ?? d.fieldTypeId) || (d.currentCrop ?? d.currentCropId)
      );

      if (!hasValidDef) {
        this.showPreconditionError();
        return;
      }

      // Update data with resolved defs
      this.data = { ...this.data, cropDefinitions: resolved };

      if (resolved.length === 1) {
        this.selectedCropDefinitionId = resolved[0].id;
      }

      this.loadInspectors();
    });

    this.loadInspectors(); // start loading inspectors in parallel
  }

  // ─── Inspector loading ────────────────────────────────────────────────────

  loadInspectors(): void {
    this.loadingInspectors = true;
    this.inspectionService.getInspectors().subscribe({
      next: (list) => {
        this.inspectors = list;
        this.loadingInspectors = false;
      },
      error: () => {
        this.inspectors = [];
        this.loadingInspectors = false;
      }
    });
  }

  getInspectorInitial(user: InspectorUser): string {
    return (user.name ?? user.email ?? '?').charAt(0).toUpperCase();
  }

  // ─── Start inspection ─────────────────────────────────────────────────────

  startInspection(): void {
    this.resolvedInspectorUserId = this.selectedInspectorId;
    this.resolvedCropDefinitionId = this.selectedCropDefinitionId;
    this.resolvedReportId = null;
    this.resolvedViewOnly = false;

    const defs = this.data.cropDefinitions ?? [];
    const selected = defs.find(d => d.id === this.selectedCropDefinitionId) ?? defs[0];
    this.resolvedCrop = selected?.crop ?? '';
    this.resolvedFieldType = selected?.fieldType ?? '';

    this.mode = 'FORM';
  }

  cancelNewInspection(): void {
    this.mode = 'LIST';
  }

  // ─── Open existing report from list ──────────────────────────────────────

  onOpenExistingReport(event: { reportId: string; viewOnly: boolean }): void {
    this.formIsDirty = false;
    this.resolvedReportId = event.reportId;
    this.resolvedViewOnly = event.viewOnly;
    this.resolvedInspectorUserId = null;
    this.resolvedCropDefinitionId = null;

    // If crop defs are already known use them, otherwise fetch
    const defs = this.data.cropDefinitions;
    if (defs && defs.length > 0) {
      const def = defs[0];
      this.resolvedCrop = def.crop;
      this.resolvedFieldType = def.fieldType;
      this.mode = 'FORM';
      return;
    }

    // Fetch season data to resolve crop/fieldType for config loading
    forkJoin({
      meta: this.seasonManagerService.getSeasonMetaData().pipe(catchError(() => of(null))),
      detail: this.seasonManagerService.getDetailsOfFieldById(this.data.fieldId).pipe(catchError(() => of(null)))
    }).subscribe(({ meta, detail }) => {
      const fieldTypes: any[] = meta?.seasonFieldType ?? [];
      const cropTypes: any[] = meta?.seasonFieldCrop ?? [];

      // Use details array if available, fall back to single detail object
      const rawDetails: any[] = (detail?.details?.length > 0)
        ? detail.details
        : (detail?.detail ? [detail.detail] : []);

      const first = rawDetails[0];
      if (first) {
        const ftId = first.fieldType ?? first.fieldTypeId;
        const cropId = first.currentCrop ?? first.currentCropId;
        this.resolvedFieldType = fieldTypes.find((t: any) => t.id === ftId)?.description
          ?? (ftId ? String(ftId) : '');
        this.resolvedCrop = cropTypes.find((c: any) => c.id === cropId)?.description
          ?? (cropId ? String(cropId) : '');
      }

      this.mode = 'FORM';
    });
  }

  // ─── New inspection from list ─────────────────────────────────────────────

  onCreateNewInspection(): void {
    this.formIsDirty = false;
    this.selectedInspectorId = null;
    this.selectedCropDefinitionId = null;
    this.resolvedInspectorUserId = null;
    this.resolvedCropDefinitionId = null;
    this.resolvedCrop = '';
    this.resolvedFieldType = '';
    this.resolvedReportId = null;
    this.resolvedViewOnly = false;
    this.openNewInspectionDialog();
  }

  // ─── Back navigation ──────────────────────────────────────────────────────

  goBack(): void {
    if (this.mode === 'FORM') {
      this.mode = this.data.isNewInspection ? 'NEW_INSPECTION' : 'LIST';
    } else if (this.mode === 'NEW_INSPECTION') {
      this.mode = 'LIST';
    }
  }

  // ─── Precondition error ───────────────────────────────────────────────────

  showPreconditionError(): void {
    this.mode = 'LIST';
    const ref = this.dialog.open(PreconditionErrorDialogComponent, {
      width: '400px',
      autoFocus: false,
      data: {}
    });
    ref.afterClosed().subscribe(() => this.dialogRef.close());
  }

  // ─── Close guard ──────────────────────────────────────────────────────────

  onFormDirtyChange(dirty: boolean): void {
    this.formIsDirty = dirty;
  }

  requestClose(): void {
    if (this.formIsDirty) {
      const ref = this.dialog.open(UnsavedChangesDialogComponent, {
        width: '360px',
        autoFocus: false
      });
      ref.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) this.dialogRef.close();
      });
    } else {
      this.dialogRef.close();
    }
  }
}

// ─── Helper dialogs (declared in app.module) ─────────────────────────────────

@Component({
  selector: 'app-precondition-error-dialog',
  template: `
    <div class="p-6 max-w-sm">
      <h3 class="text-base font-semibold text-gray-800 mb-2">Cannot Create Inspection</h3>
      <p class="text-sm text-gray-500 mb-5">
        This field is missing crop or field type information for the active season.
        Please update field details before creating a Field Inspection.
      </p>
      <div class="flex justify-end">
        <button mat-flat-button color="primary" [mat-dialog-close]="true">OK</button>
      </div>
    </div>`,
  standalone: false
})
export class PreconditionErrorDialogComponent {}

@Component({
  selector: 'app-unsaved-changes-dialog',
  template: `
    <div class="p-6 max-w-sm">
      <h3 style="font-size:16px; font-weight:600; color:#1a1a1a; margin-bottom:8px;">Unsaved Changes</h3>
      <p class="text-sm text-gray-500 mb-5">{{ message }}</p>
      <div class="flex justify-end gap-3">
        <button mat-stroked-button style="white-space:nowrap; min-width:110px;" [mat-dialog-close]="false">{{ keepLabel }}</button>
        <!-- color="warn" resolves to --warn, #316A9F, which is a mid blue in this
             theme rather than a red, while the label still comes from the error
             palette and lands dark on it. White has to be forced through
             --mdc-filled-button-label-text-color: Material sets the label
             explicitly, so plain color alone does not reach it. -->
        <button mat-flat-button color="warn" [mat-dialog-close]="true"
                style="white-space:nowrap; min-width:110px; color:#fff; --mdc-filled-button-label-text-color:#fff;">{{ discardLabel }}</button>
      </div>
    </div>`,
  standalone: false
})
export class UnsavedChangesDialogComponent {
  /**
   * Wording is per caller, because what the user is walking away from differs.
   * Closing a dialog and switching a tab need different verbs, and a button that
   * says "Close Anyway" when it is about to switch a tab is simply wrong.
   *
   * Defaults reproduce the original text exactly, so the field inspection dialog,
   * which passes no data, is unchanged.
   */
  message: string;
  keepLabel: string;
  discardLabel: string;

  constructor(@Inject(MAT_DIALOG_DATA) data: any) {
    this.message = data?.message
      ?? 'You have unsaved changes. Are you sure you want to close? Your changes will be lost.';
    this.keepLabel = data?.keepLabel ?? 'Keep Editing';
    this.discardLabel = data?.discardLabel ?? 'Close Anyway';
  }
}
